import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { GridGroupNode, GridRenderCellParams, useGridApiContext } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { AuditRow } from "../../../models/AuditRow";
import { AuditTrip } from "../../../models/AuditTrip";
import { AUDIT_TOKENS } from "../../../theme/auditTokens";
import { StatusPill } from "../../common/StatusPill";

const INFO_GLYPH = "i";

interface Props extends GridRenderCellParams<AuditRow> {
  onShowDetail: (trip: AuditTrip) => void;
}

/**
 * First (pinned) column: expand toggle, vehicle name and one Digital/Analog pill per record type
 * present on the day; the vehicle-check result sits in the pill tooltip. Trip rows show their
 * own record type pill and an info button that opens the trip detail overlay.
 */
export function VehicleGroupingCell({ row, rowNode, onShowDetail }: Props) {
  const apiRef = useGridApiContext();
  const { t } = useTranslation();

  if (row.rowType === "trip") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: "34px", minWidth: 0 }}>
        <Box component="span" sx={{ color: AUDIT_TOKENS.muted, overflow: "hidden", textOverflow: "ellipsis" }}>
          {row.device.name}
        </Box>
        <StatusPill tone={row.trip.recordType}>{t(`logbookAudit.recordType.${row.trip.recordType}`)}</StatusPill>
        <Box
          component="button"
          type="button"
          onClick={() => onShowDetail(row.trip)}
          aria-label={t("logbookAudit.detail.open")}
          title={t("logbookAudit.detail.open")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: `1px solid ${AUDIT_TOKENS.warn.fg}`,
            color: AUDIT_TOKENS.warn.fg,
            bgcolor: AUDIT_TOKENS.warn.bg,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
            p: 0,
            flexShrink: 0,
            "&:hover": { filter: "brightness(.95)" },
            "&:focus-visible": { outline: AUDIT_TOKENS.focusRing, outlineOffset: 1 },
          }}
        >
          {INFO_GLYPH}
        </Box>
      </Box>
    );
  }

  const groupNode = rowNode as GridGroupNode;
  const expanded = !!groupNode.childrenExpanded;
  const status = row.device.vehicleCheck.status;
  const pillTitle =
    status === "offen"
      ? t("logbookAudit.vehicleCheck.offenTooltip")
      : t("logbookAudit.vehicleCheck.tooltip", {
          value: row.device.vehicleCheck.value || "—",
          lastCheck: row.device.lastCheck ?? "—",
        });

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <IconButton
        size="small"
        onClick={event => {
          event.stopPropagation();
          apiRef.current.setRowChildrenExpansion(row.id, !expanded);
        }}
        aria-expanded={expanded}
        aria-label={expanded ? t("logbookAudit.grid.collapseDay") : t("logbookAudit.grid.expandDay")}
        sx={{ p: "2px", color: AUDIT_TOKENS.muted }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s" }}
        />
      </IconButton>
      <Box
        component="span"
        sx={{ fontWeight: 600, color: AUDIT_TOKENS.ink, overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {row.device.name}
      </Box>
      {row.recordTypes.map(recordType => (
        <StatusPill key={recordType} tone={recordType} title={pillTitle}>
          {t(`logbookAudit.recordType.${recordType}`)}
        </StatusPill>
      ))}
    </Box>
  );
}
